import { applyScenarioToForecast } from "../lib/scenarioEngine";
import type { ForecastDataPoint } from "../types";
import type { ScenarioChange } from "../types/scenarios";
import {
	type ForecastDemoRow,
	getDemoForecastTransactionOptions,
	getDemoForecastTransactionRows,
} from "./forecastTransactionsDemoData";

/**
 * Demo-mode data for the Scenario Transactions table.
 *
 * In live mode the table is backed by the materialised `vw_ScenarioTransactions`
 * / `vw_ScenarioDataPoints` reporting views (per-scenario). There is no backend
 * in demo mode, so we derive equivalently-shaped rows from the in-memory demo
 * data + the selected scenario's adjustments:
 *  - "transactions" view ← the BuildRight demo ledger (the scenario inherits the
 *    base forecast's transactions; overlays are reflected in the balances).
 *  - "balances" view     ← applyScenarioToForecast(base, changes), i.e. the same
 *    scenario overlay curve drawn on the scenario chart.
 *
 * The shape matches ForecastRow so the shared TransactionsBalancesTable renders
 * unchanged.
 */

/** Scenario demo transactions — reuses the base demo forecast ledger. */
export function getScenarioDemoTransactionRows(): ForecastDemoRow[] {
	return getDemoForecastTransactionRows();
}

/** Distinct dropdown options for the scenario transactions view. */
export function getScenarioDemoTransactionOptions(): {
	category: string[];
	direction: string[];
	type: string[];
	layer: string[];
	status: string[];
	dataSource: string[];
} {
	return getDemoForecastTransactionOptions();
}

/**
 * Scenario demo balances — the scenario overlay curve for the selected
 * scenario, derived from the base 90-day timeline and the scenario's changes.
 */
export function getScenarioDemoBalanceRows(
	baseForecast: ForecastDataPoint[],
	changes: ScenarioChange[],
): ForecastDemoRow[] {
	const { projectedForecast } = applyScenarioToForecast(baseForecast, changes);
	return projectedForecast.map((point, index) => ({
		rowKey: `demo-scenario-bal-${index}`,
		id: index + 1,
		date: point.date.toISOString(),
		openingBalance:
			index === 0
				? point.scenarioBalance
				: projectedForecast[index - 1].scenarioBalance,
		balance: point.scenarioBalance,
		incoming: point.scenarioIncoming,
		outgoing: point.scenarioOutgoing,
		confidenceScore: null,
		contributingLayers: null,
	}));
}
